import type { GlobalSearchRequestKind } from '#shared/solr'
import type { SearchUrlParams } from '#shared/utils/search'
import type { GlobalSearchParams, SearchFilters } from '~/composables/useSolrQuery'
import { SolrParamsKey } from '#shared/solr'
import { isFetchError } from '#shared/utils/error-handling'
import { sanitizeSearchQuery } from '#shared/utils/search'
import { useRouteQuery } from '@vueuse/router'
import { getSearchErrorMessage } from '~/utils/search-error-messages'
import { getDidYouMeanSuggestion } from '~/utils/solr/spellcheck-suggestion'
import { getToast } from '~/utils/toast'

export interface SearchStateOptions {
  requestKind: GlobalSearchRequestKind
  page: Ref<number>
  filters: MaybeRef<SearchFilters>
  sort: MaybeRef<string>
}

export function useSearchState(options: SearchStateOptions) {
  const toast = getToast()
  const searchTerm = useRouteQuery<NonNullable<SearchUrlParams['sq']>>('sq', '')

  const searchParams = computed(() => buildSearchParams(searchTerm.value, options))

  const { data: solrData, isLoading, error } = useSolrSearchQuery(searchParams)

  watch(error, (err) => reportSearchError(err, toast))

  const totalResults = computed(() => solrData.value?.response?.numFound || 0)
  const searchResult = computed(() => solrData.value?.response?.docs || [])
  const highlighting = computed(() => solrData.value?.highlighting || {})
  const facets = computed(() => solrData.value?.facets)
  const transportRetried = computed(
    () => solrData.value?.searchDiagnostics?.transportRetried === true,
  )
  const hasError = computed(() => !!error.value)
  const errorStatus = computed(() => getSearchErrorStatus(error.value))
  const totalPages = computed(() => Math.ceil(totalResults.value / ITEMS_PER_PAGE))
  const hasNextPage = computed(() => options.page.value < totalPages.value)
  const hasPrevPage = computed(() => options.page.value > 1)

  const didYouMeanSuggestion = computed(() =>
    getDidYouMeanSuggestion(searchTerm.value, solrData.value?.spellcheck),
  )
  const spellcheckSuggestion = computed(() => didYouMeanSuggestion.value?.query ?? null)

  return {
    term: searchTerm,
    searchResult,
    highlighting,
    totalResults,
    totalPages,
    hasNextPage,
    hasPrevPage,
    facets,
    isLoading,
    transportRetried,
    hasError,
    errorStatus,
    spellcheckSuggestion,
    didYouMeanSuggestion,
  }
}

function getSearchErrorStatus(err: unknown): number | null {
  if (!err || typeof err !== 'object') {
    return null
  }

  const fetchError = err as {
    status?: unknown
    statusCode?: unknown
    response?: { status?: unknown }
  }
  const status = fetchError.status ?? fetchError.statusCode ?? fetchError.response?.status
  return typeof status === 'number' ? status : null
}

function reportSearchError(err: unknown, toast: ReturnType<typeof getToast>) {
  if (!err) {
    return
  }

  if (isFetchError(err, 400)) {
    const serverMsg = err.data?.message || err.data?.statusMessage
    toast.error(getSearchErrorMessage(serverMsg))
  } else {
    toast.error('Søket feilet')
  }
}

function buildSearchParams(query: string, options: SearchStateOptions): GlobalSearchParams {
  return {
    [SolrParamsKey.GlobalSearch]: {
      requestKind: options.requestKind,
      q: sanitizeSearchQuery(query),
      rows: Math.min(ITEMS_PER_PAGE, 100),
      start: (Math.max(1, options.page.value) - 1) * ITEMS_PER_PAGE,
      filters: toValue(options.filters),
      sort: toValue(options.sort) || undefined,
    },
  }
}
